/** Session flag so we only auto-recover once per navigation attempt (avoids reload loops). */
export const CHUNK_RELOAD_ONCE_KEY = 'bizcare_chunk_reload_once';

/** Cache-bust query param stripped after a successful boot (see main.ts). */
export const CHUNK_RELOAD_QUERY_PARAM = '__chunkReload';

const DEV_RESTART_HINT =
    '[BizCare] Lazy route failed to load after recovery. Stop the dev server, run "npm start", then hard-refresh (Ctrl+Shift+R).';

const BUILD_ASSET_URL_RE =
    /(?:^|\/)(?:chunk|main|polyfills|runtime|styles)[-.][^/?#]+\.(?:js|css|mjs)(?:$|[?#])/i;

/** True when a URL looks like a hashed Angular build asset. */
export function isBuildAssetUrl(url: string | null | undefined): boolean {
    if (!url) {
        return false;
    }
    try {
        const path = new URL(url, window.location.origin).pathname;
        return BUILD_ASSET_URL_RE.test(path);
    } catch {
        return BUILD_ASSET_URL_RE.test(url);
    }
}

function textLooksLikeChunkLoadFailure(text: string): boolean {
    const normalized = text.toLowerCase();
    return (
        normalized.includes('failed to fetch dynamically imported module') ||
        normalized.includes('error loading dynamically imported module') ||
        normalized.includes('importing a module script failed') ||
        normalized.includes('dynamically imported module') ||
        normalized.includes('failed to load module script') ||
        normalized.includes('mime type') ||
        (normalized.includes('module script') && normalized.includes('text/html')) ||
        normalized.includes('chunkloaderror') ||
        (normalized.includes('loading chunk') && normalized.includes('failed'))
    );
}

/** Detect stale/missing lazy-route chunks in dev and production. */
export function isLazyChunkLoadError(errorLike: unknown): boolean {
    if (errorLike == null) {
        return false;
    }

    if (typeof ErrorEvent !== 'undefined' && errorLike instanceof ErrorEvent) {
        if (isBuildAssetUrl(errorLike.filename)) {
            return true;
        }
        const target = errorLike.target;
        if (target instanceof HTMLScriptElement && isBuildAssetUrl(target.src)) {
            return true;
        }
        if (target instanceof HTMLLinkElement && isBuildAssetUrl(target.href)) {
            return true;
        }
    }

    const parts: string[] = [];

    if (typeof errorLike === 'string') {
        parts.push(errorLike);
    }

    if (errorLike instanceof Error) {
        parts.push(errorLike.name, errorLike.message);
    }

    const record = errorLike as {
        message?: string;
        name?: string;
        type?: string;
        error?: unknown;
        filename?: string;
        target?: EventTarget | null;
    };
    if (record.message) {
        parts.push(record.message);
    }
    if (record.name) {
        parts.push(record.name);
    }
    if (record.type) {
        parts.push(record.type);
    }
    if (record.filename) {
        parts.push(record.filename);
    }
    if (record.error != null) {
        parts.push(String((record.error as Error)?.message ?? record.error));
    }
    if (record.target instanceof HTMLScriptElement) {
        parts.push(record.target.src);
    }
    if (record.target instanceof HTMLLinkElement) {
        parts.push(record.target.href);
    }

    return textLooksLikeChunkLoadFailure(parts.join(' '));
}

/** Remove the chunk-recovery cache-bust param so the URL stays clean after reload. */
export function stripChunkReloadQueryParam(): void {
    if (typeof window === 'undefined') {
        return;
    }

    const url = new URL(window.location.href);
    if (!url.searchParams.has(CHUNK_RELOAD_QUERY_PARAM)) {
        return;
    }

    url.searchParams.delete(CHUNK_RELOAD_QUERY_PARAM);
    const search = url.searchParams.toString();
    const cleanUrl = url.pathname + (search ? `?${search}` : '') + url.hash;
    window.history.replaceState(window.history.state, '', cleanUrl);
}

export function clearChunkReloadGuard(): void {
    if (typeof window === 'undefined') {
        return;
    }
    window.sessionStorage.removeItem(CHUNK_RELOAD_ONCE_KEY);
}

async function navigateWithCacheBypass(): Promise<void> {
    const currentUrl = window.location.href;

    try {
        await fetch(currentUrl, { cache: 'reload', credentials: 'same-origin' });
    } catch {
        // Still navigate below if prefetch fails (offline, etc.).
    }

    const url = new URL(currentUrl);
    url.searchParams.delete(CHUNK_RELOAD_QUERY_PARAM);
    url.searchParams.set(CHUNK_RELOAD_QUERY_PARAM, String(Date.now()));
    window.location.replace(url.toString());
}

/**
 * Clears HTTP/SW caches and reloads with cache bypass when a lazy chunk is missing.
 * Returns true when a reload was triggered.
 */
export async function recoverFromChunkLoadError(): Promise<boolean> {
    if (typeof window === 'undefined') {
        return false;
    }

    const alreadyReloaded = window.sessionStorage.getItem(CHUNK_RELOAD_ONCE_KEY) === '1';
    if (alreadyReloaded) {
        window.sessionStorage.removeItem(CHUNK_RELOAD_ONCE_KEY);
        console.warn(DEV_RESTART_HINT);
        return false;
    }

    window.sessionStorage.setItem(CHUNK_RELOAD_ONCE_KEY, '1');

    try {
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((name) => caches.delete(name)));
        }

        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((registration) => registration.unregister()));
        }
    } catch {
        // Continue with hard navigation even if cache/SW cleanup fails.
    }

    await navigateWithCacheBypass();
    return true;
}

/** Register global listeners before Angular boot (script load / HMR chunk errors). */
export function initChunkLoadRecoveryListeners(): void {
    if (typeof window === 'undefined') {
        return;
    }

    window.addEventListener(
        'error',
        (event) => {
            const errorEvent = event as ErrorEvent;
            if (!isLazyChunkLoadError(errorEvent) && !isLazyChunkLoadError(errorEvent.error ?? errorEvent.message)) {
                return;
            }
            event.preventDefault();
            void recoverFromChunkLoadError();
        },
        true,
    );

    window.addEventListener('unhandledrejection', (event) => {
        if (!isLazyChunkLoadError(event.reason)) {
            return;
        }
        event.preventDefault();
        void recoverFromChunkLoadError();
    });
}
